import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { Button, Chip, EmptyState, Skeleton } from '../components/ui'
import { usePersonDuplicates } from '../api/hooks'
import type { DuplicateGroup } from '../api/types'
import { useAuthStore } from '../stores/auth'

/** Wizard URL for a set of persons (the wizard suggests the survivor). */
export function mergeWizardHref(persons: string[]): string {
  return `/directory/merge?persons=${persons.map(encodeURIComponent).join(',')}`
}

/** Operators and admins may merge; viewers only read. */
export function useCanOperate(): boolean {
  return useAuthStore((s) => s.customer?.role === 'admin' || s.customer?.role === 'operator')
}

function GroupRow({ group, canOperate }: { group: DuplicateGroup; canOperate: boolean }) {
  const navigate = useNavigate()
  return (
    <li className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 md:flex-row md:items-center" data-testid="duplicate-group">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ostrel-ink">
            {group.location?.name ?? 'No shared location'}
          </span>
          {group.access_differs ? (
            <Chip tone="warn" label="Access differs" />
          ) : (
            <Chip tone="ok" label="Same access" />
          )}
          {group.blockers.map((b) => (
            <Chip key={`${b.code}-${b.system.unique_id}`} tone="danger" label={`blocked · ${b.system.name}`} title={b.message} />
          ))}
          {group.warnings.map((w) => (
            <Chip key={w.code} tone="danger" label={w.code.replace(/_/g, ' ')} title={w.message} />
          ))}
        </div>
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {group.persons.map((p) => (
            <li key={p.unique_id} className="tnum flex flex-wrap items-center gap-2 text-[11px] text-ostrel-ink-muted">
              <Link to={`/directory/${p.unique_id}`} className="text-ostrel-ink hover:text-ostrel-accent">
                {p.display_name || 'Unnamed'}
              </Link>
              <span className="font-mono">{p.unique_id}</span>
              {p.unique_id === group.survivor && <Chip tone="gold" label="suggested survivor" />}
              {p.is_placeholder && <Chip tone="muted" label="placeholder" />}
              {p.status !== 'active' && <Chip tone="muted" label={p.status} />}
              <span>
                {p.credentials} cred · {p.occupancies} occ · {p.events} events
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Button
        variant="navy"
        size="sm"
        disabled={!canOperate}
        title={canOperate ? undefined : 'Only operators can merge people'}
        onClick={() => navigate(mergeWizardHref(group.persons.map((p) => p.unique_id)))}
      >
        Review &amp; merge
      </Button>
    </li>
  )
}

/** `/directory/duplicates` — detected duplicate people, each group opening the
 *  guided merge wizard. */
export default function PersonDuplicatesPage() {
  const duplicates = usePersonDuplicates()
  const canOperate = useCanOperate()

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Directory" title="Merge duplicates">
        <p className="tnum mt-1 text-xs text-ostrel-ink-muted">
          <Link to="/directory" className="hover:text-ostrel-gold">
            People
          </Link>
          {' / '}Duplicates
        </p>
      </PageHeader>

      <p className="max-w-3xl text-xs leading-relaxed text-ostrel-ink-muted">
        People who live at the same place under the same name (or under flat-number placeholder names). Review a group
        to pick who to keep, compare their access and decide what the merged person may open.
      </p>

      {duplicates.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : duplicates.isError ? (
        <EmptyState title="Could not load duplicates" hint={duplicates.error.message} />
      ) : duplicates.data.length === 0 ? (
        <EmptyState title="No duplicates found" hint="No people share a location and a name." />
      ) : (
        <section className="rounded-sm border border-ostrel-line bg-ostrel-card p-4">
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {duplicates.data.map((g) => (
              <GroupRow key={g.persons.map((p) => p.unique_id).join(',')} group={g} canOperate={canOperate} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
